import { FormElement, SearchSelectOptionsGroup } from '@tradein/api';
import { Ref } from 'vue';
import i18nManager from '../../composables/i18nManager';
import setup from '../../composables/setup';
import { scrollToElement as scrollToElementHelper } from '@tradein/utils';

const { currentMediaQuery } = setup();
const { i18n } = i18nManager();

export interface SearchSelectElement extends HTMLElement {
  options: SearchSelectOptionsGroup[];
}

export const setSearchSelectOptions = (
  searchSelectControl: Ref<SearchSelectElement>,
  element: FormElement,
): void => {
  if (element.type === 'SearchSelect') {
    if (searchSelectControl.value && element.field.searchSelectOptions) {
      searchSelectControl.value.options = element.field.searchSelectOptions;

      searchSelectControl.value.options.forEach((option) => {
        if (option.label) {
          option.label = i18n.value.t(option.label).toString();
        }
      });
    }
  }
};

export const getSearchSelectLabel = (
  value: string,
  element: FormElement,
): string => {
  if (element.field.searchSelectOptions) {
    for (const searchSelectOptionGroup of element.field.searchSelectOptions) {
      for (const option of searchSelectOptionGroup.options) {
        if (value === option.value) {
          return option.label;
        }
      }
    }
  }

  return '';
};

export const scrollToElement = (
  e: Event,
  formElement: FormElement,
  element: Element,
): void => {
  if (
    formElement.type === 'Text' ||
    formElement.type === 'Textarea' ||
    formElement.type === 'SearchSelect' ||
    formElement.type === 'Month' ||
    formElement.type === 'Date' ||
    formElement.type === 'Email' ||
    formElement.type === 'Number' ||
    formElement.type === 'Tel'
  ) {
    // scrollToElement needs to be enabled for iOS Devices when clicking on the calendar icon
    const clickedElement = (e.target as HTMLElement)?.tagName.toLowerCase();
    const enableSafari =
      clickedElement === 'wb-icon' && formElement.type === 'Month';

    scrollToElementHelper(element, 110, {
      disableSafari: !enableSafari,
      currentMediaQuery: currentMediaQuery.value,
      enableForMediaQuery: ['mq1', 'mq2'],
    });
  }
};

const isMercedesVin = (vin: string) => {
  return ['WDC', 'WDD', 'WDF', 'WMX', 'WDB', 'W1K', 'W1N', 'WMX'].includes(
    vin.toUpperCase().slice(0, 3),
  );
};

// -------------------- Mercedes Vin Hint Utils -------------------- //

const MERCEDES_VIN_HINT_TIMESTAMP = 'tradein-vin-hint-timestamp';
const MERCEDES_VIN_HINT_THRESHOLD_DAYS = 1;

export const setVinHintTimestamp = () => {
  localStorage.setItem(MERCEDES_VIN_HINT_TIMESTAMP, Date.now().toString());
};

const checkIfVinHintTimeoutReached = () => {
  const timestamp = localStorage.getItem(MERCEDES_VIN_HINT_TIMESTAMP);
  try {
    if (timestamp && !Number.isNaN(Number.parseInt(timestamp))) {
      const timeoutReached =
        Date.now() - Number.parseInt(timestamp) >
        MERCEDES_VIN_HINT_THRESHOLD_DAYS * 24 * 60 * 60 * 1000;
      return timeoutReached;
    } else {
      // always show hint when no timestamp was saved or timestamp is not a number
      return true;
    }
  } catch (e) {
    console.error(
      'Mercedes Vin Hint Timout cannot be parsed from locale storage key ',
      MERCEDES_VIN_HINT_TIMESTAMP,
    );
    // always show hint when timestamp cannot be parsed
    return true;
  }
};

export const showMercedesVinHint = (
  vin: string,
  valid: boolean | undefined,
) => {
  const timeoutReached = checkIfVinHintTimeoutReached();
  if (!timeoutReached) return false;
  return isMercedesVin(vin) && !valid;
};
